import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Icon,
  IconButton,
  Image,
  ModalWithTitle,
  Paper,
  TextField,
  Theme,
  Typography,
  useTheme
} from "@outschool/backpack";
import { ActivitySubject } from "@outschool/business-rules";
import {
  ChildInput,
  CurrentUserQueryQuery
} from "@outschool/gql-frontend-generated";
import {
  IconDefinition,
  faCalendar,
  faHeart,
  faPencil,
  faPlus,
  faPlusCircle,
  faSchool,
  faUser
} from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { useSession, useTrackLearnerCreated } from "@outschool/ui-auth";
import { PickLearnerAvatarModal } from "@outschool/ui-components-classroom";
import {
  AvatarImage,
  DEFAULT_AVATAR,
  ErrorMessage,
  getRandomAvatar
} from "@outschool/ui-components-shared";
import React, { useState } from "react";

import patterns from "../../../../images/banners/welcome/patterns.png";
import {
  useAddChildMutation,
  useUpdateChildMutation
} from "../../../hooks/Learners";
import { useStickyHeader } from "../../../hooks/useStickyHeader";
import { useStickyState } from "../../../hooks/useStickyState";
import { useActiveLearner } from "../../../providers/ActiveLearnerProvider";
import { TIMES_OF_DAY } from "../../AvailableTimesInput/utils";
import { DateOfBirthPicker } from "../../learner/DateOfBirthPicker";
import { LearnerNameWarning } from "../../learner/LearnerNameWarning";
import { MultiSelectSubjects } from "../../onboarding/components/MultiSelectSubjects";
import {
  CLASS_SIZE_SELECTIONS,
  LEARNING_FORMAT_SELECTIONS,
  LEARNING_NEEDS_SELECTIONS,
  LearningNeedsType,
  SCHOOLING_APPROACH_SELECTIONS
} from "../../onboarding/consts";
import { findSelectedTimeOptionFromAvailableTimes } from "../../onboarding/utils";
import { AttributeCheckboxList } from "./AttributeCheckboxList";
import AvailableTimesInput from "./AvailableTimesInput";
import { SchoolingApproachButtons } from "./SchoolingApproachButtons";
import { getLabelFromValue } from "./utils";

type ChildInputWithoutInterests = Omit<ChildInput, "interestTopics"> & {};

const AccordionPanels = {
  schoolingApproach: "schoolingApproach",
  interests: "interests",
  setup: "setup",
  availability: "availability",
  needs: "needs"
};

export const AddLearnerBanner = () => {
  const { t } = useTranslation("client\\components\\loggedInHome\\Dashboard");
  const [error, setError] = useState<string | undefined>();
  const theme = useTheme();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const availabilityRef = React.useRef<HTMLElement | null>(null);

  const [expandedAccordion, setExpandedAccordion] = React.useState<
    string | null
  >(null);
  const handleChangeAccordion = (panel: string, isExpanded: boolean) => {
    setExpandedAccordion(isExpanded ? panel : null);
  };

  const [showPickAvatarModal, setShowPickAvatarModal] = useState(false);
  const [child, setChild] = useState<ChildInputWithoutInterests>({});
  const [childNameHasChanged, setChildNameHasChanged] = useState(false);
  const [childSubjects, setChildSubjects] = useState<Array<string>>([]);
  const [otherAttributes, setOtherAttributes] = useState<
    Array<{ key: string; value: string }>
  >([]);
  const [schoolingApproach, setSchoolingApproach] = useState<string>("");
  const [childNeeds, setChildNeeds] = useState<Array<string>>([]);
  const [childFormats, setChildFormats] = useState<Array<string>>([]);
  const [childClassSizes, setChildClassSizes] = useState<Array<string>>([]);

  const openModal = React.useCallback(
    (
      childInstance?: NonNullable<
        CurrentUserQueryQuery["currentUser"]
      >["children"][0]
    ) => {
      setIsModalOpen(true);
      if (!!childInstance) {
        setChild({
          ...childInstance,
          availableTimes:
            childInstance.entityAvailabilitySchedule?.entityAvailabilities || []
        });

        setChildSubjects(
          (
            childInstance.attributes?.filter(
              a => a.key === "subject_interest"
            ) || []
          ).map(a => a.value)
        );

        setSchoolingApproach(
          childInstance.attributes?.find(a => a.key === "schooling_approach")
            ?.value || ""
        );

        setChildNeeds(
          (
            childInstance.attributes?.filter(a => a.key === "learning_need") ||
            []
          ).map(a => a.value)
        );

        setChildFormats(
          (
            childInstance.attributes?.filter(a => a.key === "class_format") ||
            []
          ).map(a => a.value)
        );

        setChildClassSizes(
          (
            childInstance.attributes?.filter(a => a.key === "class_size") || []
          ).map(a => a.value)
        );

        setOtherAttributes(
          childInstance.attributes?.filter(
            a =>
              a.key !== "schooling_approach" &&
              a.key !== "schooling_approach_type" &&
              a.key !== "subject_interest" &&
              a.key !== "learning_need" &&
              a.key !== "class_format" &&
              a.key !== "class_size"
          ) || []
        );
      } else {
        setChild({ avatar: getRandomAvatar() });
      }
    },
    [
      setIsModalOpen,
      setChild,
      setChildSubjects,
      setSchoolingApproach,
      setChildNeeds,
      setChildFormats,
      setChildClassSizes,
      setOtherAttributes
    ]
  );
  const closeModal = React.useCallback(() => {
    setIsModalOpen(false);
    setChild({});
    setExpandedAccordion(null);
    setError(undefined);
    setSchoolingApproach("");
    setChildSubjects([]);
    setChildNeeds([]);
    setChildFormats([]);
    setChildClassSizes([]);
    setOtherAttributes([]);
    setChildNameHasChanged(false);
  }, [
    setIsModalOpen,
    setExpandedAccordion,
    setSchoolingApproach,
    setOtherAttributes,
    setChildSubjects,
    setChildNeeds,
    setChildFormats,
    setChildClassSizes,
    setChildNameHasChanged
  ]);

  const { updateChild, loading: updateLoading } = useUpdateChildMutation();
  const { addChild, loading: addLoading } = useAddChildMutation();

  const trackLearnerCreated = useTrackLearnerCreated();

  const schoolingApproachText = React.useMemo(() => {
    if (schoolingApproach && schoolingApproach.length > 0) {
      return getLabelFromValue({
        value: schoolingApproach,
        selections: SCHOOLING_APPROACH_SELECTIONS
      });
    }
    return "Choose approach";
  }, [schoolingApproach]);

  const upsertChild = React.useCallback(async () => {
    // check if we have the minimum child attributes
    if (!child.name || !child.birthMonth || !child.birthYear) {
      // can't create child without these
      return;
    }

    const attributes = [
      ...otherAttributes,
      ...(schoolingApproach
        ? [
            {
              key: "schooling_approach",
              value: schoolingApproach
            }
          ]
        : []),
      ...childSubjects.map(a => ({
        key: "subject_interest",
        value: a
      })),
      ...childNeeds.map(a => ({
        key: "learning_need",
        value: a
      })),
      ...childFormats.map(a => ({
        key: "class_format",
        value: a
      })),
      ...childClassSizes.map(a => ({
        key: "class_size",
        value: a
      }))
    ];

    const upsertChild = {
      ...child,
      attributes
    };

    try {
      if (child.uid) {
        await updateChild(upsertChild);
      } else {
        await addChild(upsertChild);
        trackLearnerCreated();
      }

      closeModal();
    } catch (err) {
      setError(err);
    }
  }, [
    child,
    addChild,
    updateChild,
    schoolingApproach,
    childSubjects,
    childNeeds,
    childFormats,
    childClassSizes,
    otherAttributes,
    closeModal,
    trackLearnerCreated
  ]);

  return (
    <>
      <LearnerBanner openModal={openModal} />
      <ModalWithTitle
        closeButtonArialLabel={t("close add learner form")}
        headerTitle={
          <Typography variant="h6" sx={{ color: theme.palette.neutral[900] }}>
            {child.uid
              ? t`Edit learner`
              : `Add your learner to get the best classes`}
          </Typography>
        }
        open={isModalOpen}
        onClose={closeModal}
        sx={{
          maxWidth: 700,
          mx: "auto",
          display: "flex",
          flexDirection: "column"
        }}
        modalHeaderProps={{
          sx: {
            // eslint-disable-next-line i18next/no-literal-string
            borderBottom: "none",
            color: theme.palette.neutral[700],
            paddingBottom: 0
          }
        }}
      >
        <Box
          sx={{
            display: "flex",
            width: "100%",
            flexDirection: "column",
            px: "1.6rem",
            pb: "1.6rem",
            // account for header height and place scroll on inner container
            maxHeight: "calc(100% - 50px)"
          }}
        >
          <ErrorMessage value={error} />
          <Box
            sx={{
              marginTop: "0.5em",
              padding: "1em",
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: "1em",
              border: `1px solid ${theme.palette.primary[50]}`,
              borderRadius: "0.5em",
              boxShadow: "0px 1px 8px 0px #0000000A",
              overflowY: "auto"
            }}
          >
            {/* avatar */}
            <Box
              flex
              sx={{
                margin: "auto",
                flexDirection: "column",
                position: "relative",
                width: 80,
                height: 80
              }}
            >
              <AvatarImage
                avatar={child.avatar || DEFAULT_AVATAR}
                height={80}
                width={80}
                sx={{ alignSelf: "center" }}
              />
              <Button
                sx={{
                  backgroundColor: `${theme.palette.common.white} !important`,
                  color: theme.palette.neutral[600],
                  fontSize: "0.75em",
                  opacity: "0.74",
                  position: "absolute",
                  top: 25,
                  left: 17,
                  width: 46,
                  height: 28
                }}
                variant="text"
                onClick={() => setShowPickAvatarModal(true)}
                size="small"
              >
                {t("EDIT")}
              </Button>
            </Box>
            {/* name and age */}
            <Box>
              <Box
                flex
                sx={{
                  gap: "0.5em",
                  [theme.breakpoints.down("sm")]: {
                    flexWrap: "wrap",
                    gap: "1em"
                  }
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%"
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "1.2rem", color: "grey.500" }}
                  >
                    {t("FIRST NAME")}
                  </Typography>
                  <TextField
                    autoComplete="off"
                    size="small"
                    placeholder={t("First name")}
                    value={child.name || ""}
                    onChange={e => {
                      setChild({ ...child, name: e.target.value });
                      setChildNameHasChanged(true);
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    [theme.breakpoints.down("sm")]: {
                      minWidth: 250
                    }
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "1.2rem", color: "grey.500" }}
                  >
                    {t("BIRTHDAY")}
                  </Typography>
                  <DateOfBirthPicker
                    birthMonth={child.birthMonth}
                    birthYear={child.birthYear}
                    onChange={(birthMonth: number, birthYear: number) => {
                      setChild({ ...child, birthMonth, birthYear });
                    }}
                    selectSx={{ width: "100%", marginBottom: 0 }}
                    highlightErrors={false}
                  />
                </Box>
              </Box>
              {childNameHasChanged && (
                <LearnerNameWarning name={child.name || ""} />
              )}
            </Box>
            {/* Schooling approach */}
            <Box>
              <Typography
                variant="body2"
                sx={{ fontSize: "1.2rem", color: "grey.500" }}
              >
                {t("SCHOOLING APPROACH")}
              </Typography>
              <Accordion
                expanded={
                  expandedAccordion === AccordionPanels.schoolingApproach
                }
                onChange={(_event: React.SyntheticEvent, isExpanded: boolean) =>
                  handleChangeAccordion(
                    AccordionPanels.schoolingApproach,
                    isExpanded
                  )
                }
              >
                <StyledAccordionSummary
                  icon={faSchool}
                  text={schoolingApproachText}
                  isExpanded={
                    expandedAccordion === AccordionPanels.schoolingApproach
                  }
                />
                <AccordionDetails sx={{ marginTop: "1em" }}>
                  <SchoolingApproachButtons
                    handleChangeApproach={approach => {
                      setSchoolingApproach(approach);
                    }}
                    selectedSchoolingApproach={schoolingApproach}
                  />
                </AccordionDetails>
              </Accordion>
            </Box>

            {/* Interests */}
            <Box>
              <Typography
                variant="body2"
                sx={{ fontSize: "1.2rem", color: "grey.500" }}
              >
                {t("INTERESTS")}
              </Typography>
              <Accordion
                expanded={expandedAccordion === AccordionPanels.interests}
                onChange={(_event: React.SyntheticEvent, isExpanded: boolean) =>
                  handleChangeAccordion(AccordionPanels.interests, isExpanded)
                }
              >
                <StyledAccordionSummary
                  icon={faHeart}
                  text={
                    childSubjects.length
                      ? childSubjects.join(", ")
                      : t`Select interests`
                  }
                  isExpanded={expandedAccordion === AccordionPanels.interests}
                />
                <AccordionDetails sx={{ marginTop: "1em" }}>
                  <MultiSelectSubjects
                    selectedSubjects={childSubjects as ActivitySubject[]}
                    setSelectedSubjects={setChildSubjects}
                  />
                </AccordionDetails>
              </Accordion>
            </Box>
            {/* Class setup */}
            <Box>
              <Typography
                variant="body2"
                sx={{ fontSize: "1.2rem", color: "grey.500" }}
              >
                {t("CLASS SETUP")}
              </Typography>
              <Accordion
                expanded={expandedAccordion === AccordionPanels.setup}
                onChange={(_event: React.SyntheticEvent, isExpanded: boolean) =>
                  handleChangeAccordion(AccordionPanels.setup, isExpanded)
                }
              >
                <StyledAccordionSummary
                  icon={faHeart}
                  text={
                    childFormats.length || childClassSizes.length
                      ? childFormats
                          .map(format =>
                            getLabelFromValue({
                              value: format,
                              selections: LEARNING_FORMAT_SELECTIONS
                            })
                          )
                          .concat(
                            childClassSizes.map(size =>
                              getLabelFromValue({
                                value: size,
                                selections: CLASS_SIZE_SELECTIONS
                              })
                            )
                          )
                          .join(", ")
                      : t`Select class preferences`
                  }
                  isExpanded={expandedAccordion === AccordionPanels.setup}
                />
                <AccordionDetails sx={{ marginTop: "1em" }}>
                  <Box
                    sx={{
                      marginTop: "1.25em",
                      marginLeft: "0.5em",
                      display: "flex",
                      [theme.breakpoints.down("sm")]: {
                        flexDirection: "column",
                        gap: "1em"
                      }
                    }}
                  >
                    <Box sx={{ marginRight: "1em", flex: 1 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{ marginBottom: "0.25em" }}
                      >{t`Class format`}</Typography>
                      <AttributeCheckboxList
                        selections={LEARNING_FORMAT_SELECTIONS}
                        selectedValues={childFormats}
                        setValues={setChildFormats}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{ marginBottom: "0.25em" }}
                      >{t`Class size`}</Typography>
                      <AttributeCheckboxList
                        selections={CLASS_SIZE_SELECTIONS}
                        selectedValues={childClassSizes}
                        setValues={setChildClassSizes}
                      />
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Box>
            {/* Schedule */}
            <Box>
              <Typography
                variant="body2"
                sx={{ fontSize: "1.2rem", color: "grey.500" }}
              >
                {t("SCHEDULE")}
              </Typography>
              <Accordion
                expanded={expandedAccordion === AccordionPanels.availability}
                onChange={(_event: React.SyntheticEvent, isExpanded: boolean) =>
                  handleChangeAccordion(
                    AccordionPanels.availability,
                    isExpanded
                  )
                }
              >
                <StyledAccordionSummary
                  icon={faCalendar}
                  text={
                    !child.availableTimes || child.availableTimes.length === 0
                      ? t("Set schedule")
                      : findSelectedTimeOptionFromAvailableTimes(
                          TIMES_OF_DAY,
                          child.availableTimes
                        )?.label || "Custom"
                  }
                  isExpanded={
                    expandedAccordion === AccordionPanels.availability
                  }
                />
                <AccordionDetails sx={{ marginTop: "1em" }}>
                  <AvailableTimesInput
                    availableTimes={child.availableTimes || []}
                    onUpdateAll={times => {
                      setChild({ ...child, availableTimes: times || [] });
                      setError(undefined);
                    }}
                  />
                </AccordionDetails>
              </Accordion>
            </Box>

            {/* Learning Needs */}
            <Box>
              <Typography
                variant="body2"
                sx={{ fontSize: "1.2rem", color: "grey.500" }}
              >
                {t("LEARNING NEEDS")}
              </Typography>
              <Accordion
                expanded={expandedAccordion === AccordionPanels.needs}
                onChange={(_event: React.SyntheticEvent, isExpanded: boolean) =>
                  handleChangeAccordion(AccordionPanels.needs, isExpanded)
                }
              >
                <StyledAccordionSummary
                  icon={faUser}
                  text={
                    childNeeds.length
                      ? childNeeds
                          .map(need =>
                            getLabelFromValue({
                              value: need,
                              selections: LEARNING_NEEDS_SELECTIONS
                            })
                          )
                          .join(", ")
                      : t`Select needs`
                  }
                  isExpanded={expandedAccordion === AccordionPanels.needs}
                />
                <AccordionDetails
                  sx={{ marginTop: "1em", marginLeft: "0.5em" }}
                >
                  <AttributeCheckboxList
                    selections={LEARNING_NEEDS_SELECTIONS}
                    selectedValues={childNeeds}
                    setValues={setChildNeeds}
                    specialNoneValue={LearningNeedsType.None}
                  />
                </AccordionDetails>
              </Accordion>
            </Box>
          </Box>

          <Box
            sx={{
              marginTop: "1.5em",
              marginBottom: "0.5em",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "100%"
            }}
          >
            <Button
              variant="contained"
              sx={{ width: "75%" }}
              onClick={upsertChild}
              disabled={
                !!error ||
                updateLoading ||
                addLoading ||
                !child.name ||
                !child.birthYear ||
                !child.birthMonth
              }
              ref={availabilityRef}
            >
              {t("Save")}
            </Button>
          </Box>
        </Box>
      </ModalWithTitle>

      {showPickAvatarModal && (
        <PickLearnerAvatarModal
          onSelect={avatar => {
            setShowPickAvatarModal(false);
            setChild({ ...child, avatar: avatar });
          }}
          onCancel={() => setShowPickAvatarModal(false)}
        />
      )}
    </>
  );
};

const LearnerBanner = ({
  openModal
}: {
  openModal: (child?: ChildInputWithoutInterests) => void;
}) => {
  const theme = useTheme();
  const { currentUser } = useSession();
  const { t } = useTranslation("client\\components\\loggedInHome\\Dashboard");
  const { activeLearner, setActiveLearner } = useActiveLearner();
  const { headerHeight } = useStickyHeader();
  const { isSticky, sentinelRef, stickyRef } = useStickyState();

  if (!currentUser) {
    return null;
  }

  return (
    <>
      <div ref={sentinelRef} style={{ height: "1px" }} />
      <Box
        flex
        ref={stickyRef}
        justifyContent="center"
        alignItems="center"
        sx={{
          position: "sticky",
          top: headerHeight,
          zIndex: 200,
          backgroundColor: "grey.50",
          ...(isSticky
            ? { boxShadow: "0px 1px 12px rgba(0, 0, 0, 0.08)" }
            : {}),
          transition: "box-shadow 0.2s ease-in-out",
          [theme.breakpoints.down("md")]: {
            backgroundColor: "white"
          }
        }}
      >
        <Box
          flex
          justifyContent="center"
          alignItems="center"
          sx={(theme: Theme) => ({
            width: "100%",
            position: "relative",
            maxWidth: 1120,
            "&:before, &:after": {
              content: '""',
              position: "absolute",
              top: 0,
              bottom: 0,
              width: "2.4rem",
              pointerEvents: "none",
              zIndex: 1
            },
            "&:before": {
              left: 0,
              background: `linear-gradient(to right, ${theme.palette.grey[50]}, transparent)`
            },
            "&:after": {
              right: 0,
              background: `linear-gradient(to left, ${theme.palette.grey[50]}, transparent)`
            },
            [theme.breakpoints.down("md")]: {
              "&:before, &:after": {
                width: "1.6rem"
              },
              "&:before": {
                background: "linear-gradient(to right, white, transparent)"
              },
              "&:after": {
                background: "linear-gradient(to left, white, transparent)"
              }
            }
          })}
        >
          <Box
            sx={(theme: Theme) => ({
              display: "flex",
              gap: 16,
              overflowX: "auto",
              py: 13,
              px: 24,
              [theme.breakpoints.down("md")]: {
                px: 16
              },
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": {
                display: "none"
              }
            })}
          >
            {currentUser.children.length === 0 ? (
              <Paper
                sx={{
                  borderRadius: 16,
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 16,
                  pl: 24,
                  pr: 72,
                  backgroundColor: theme.palette.primary[700],
                  cursor: "pointer",
                  position: "relative",
                  overflow: "hidden",
                  height: 76,
                  "&:hover": {
                    backgroundColor: theme.palette.primary[600]
                  },
                  "&:hover .add-learner-icon": {
                    backgroundColor: theme.palette.primary[700]
                  },
                  "&:hover .add-learner-icon-text": {
                    color: theme.palette.common.white
                  },
                  transition: "background-color 0.2s ease-in-out",
                  [theme.breakpoints.down("md")]: {
                    py: 16
                  },
                  [theme.breakpoints.down("sm")]: {
                    pl: 14
                  }
                }}
                onClick={() => {
                  openModal();
                }}
                aria-label="Add learner"
              >
                <Box
                  className="add-learner-icon"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: theme.palette.common.white,
                    borderRadius: "100%",
                    width: 42,
                    height: 42,
                    transition: "all 0.2s ease-in-out",
                    flexShrink: 0
                  }}
                >
                  <Icon
                    icon={faPlus}
                    className="add-learner-icon-text"
                    sx={{
                      color: theme.palette.primary[700],
                      fontSize: "1.25rem"
                    }}
                  />
                </Box>
                <Typography variant="h6">
                  {t("Add a learner to get class recommendations")}
                </Typography>
                <Image
                  src={patterns}
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    right: 0,
                    width: 92
                  }}
                />
              </Paper>
            ) : (
              <>
                {currentUser.children.length > 1 && (
                  <Box
                    component={"button"}
                    onClick={() => {
                      setActiveLearner(null);
                    }}
                    sx={{
                      all: "unset",
                      cursor: "pointer",
                      backgroundColor: "white",
                      borderRadius: 16,
                      px: 18,
                      flexShrink: 0,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      border: `2px solid transparent`,
                      "&:hover": {
                        border: `2px solid ${theme.palette.primary[800]}`
                      },
                      transition: "border-color 0.1s ease-in-out",
                      boxShadow: `0px 2px 6px 0px ${theme.palette.primary[600]}26`,
                      ...(!activeLearner && {
                        border: `2px solid ${theme.palette.primary[400]}`
                      })
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        [theme.breakpoints.down("md")]: {
                          fontSize: "1.4rem"
                        }
                      }}
                    >
                      {t("All learners")}
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      sx={{ color: theme.palette.grey[600] }}
                    >
                      {currentUser.children.length} {t("learners")}
                    </Typography>
                  </Box>
                )}
                {currentUser.children.map(child => (
                  <Box
                    key={child.uid}
                    sx={{
                      position: "relative",
                      borderRadius: 16,
                      backgroundColor: "white",
                      display: "flex",
                      alignItems: "center",
                      boxShadow: `0px 2px 6px 0px ${theme.palette.primary[600]}26`,
                      border: `2px solid transparent`,
                      "&:hover": {
                        border: `2px solid ${theme.palette.primary[800]}`
                      },
                      transition: "border-color 0.1s ease-in-out",
                      ...(child.uid === activeLearner?.uid && {
                        border: `2px solid ${theme.palette.primary[400]}`
                      }),
                      height: 62,
                      [theme.breakpoints.up("md")]: {
                        height: 80
                      }
                    }}
                  >
                    <Box
                      component={"button"}
                      sx={{
                        all: "unset",
                        cursor: "pointer",
                        height: "100%",
                        pl: 24,
                        pr: 72,
                        [theme.breakpoints.down("md")]: {
                          pl: 12,
                          pr: 60
                        }
                      }}
                      onClick={() => {
                        setActiveLearner(child.uid);
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 16 }}
                      >
                        <Box
                          sx={{
                            height: 60,
                            width: 60,
                            [theme.breakpoints.down("md")]: {
                              display: "none"
                            }
                          }}
                        >
                          <AvatarImage
                            avatar={child.avatar || DEFAULT_AVATAR}
                            sx={{
                              height: "100%",
                              width: "100%"
                            }}
                          />
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            color: "white",
                            fontWeight: 700
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{
                              color: "black",
                              fontWeight: 700,
                              whiteSpace: "nowrap",
                              [theme.breakpoints.down("md")]: {
                                fontSize: "1.4rem"
                              }
                            }}
                          >
                            {child.name}
                          </Typography>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              color: theme.palette.grey[500]
                            }}
                          >
                            {child.age}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    <IconButton
                      icon={faPencil}
                      variant="text"
                      sx={{
                        position: "absolute",
                        right: 20,
                        boxShadow: "none",
                        fontSize: 18,
                        borderRadius: "100%",
                        width: 36,
                        height: 36,
                        color: "black",
                        [theme.breakpoints.down("md")]: {
                          fontSize: 16,
                          right: 12
                        }
                      }}
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.stopPropagation();
                        openModal(child);
                      }}
                    />
                  </Box>
                ))}
                <IconButton
                  icon={faPlusCircle}
                  variant="text"
                  aria-label="Add learner"
                  sx={{
                    borderRadius: 16,
                    width: 40,
                    fontSize: 24
                  }}
                  onClick={() => {
                    openModal();
                  }}
                />
              </>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

const StyledAccordionSummary = ({
  text,
  icon,
  isExpanded
}: {
  text: string;
  icon: IconDefinition;
  isExpanded: boolean;
}) => {
  const theme = useTheme();

  return (
    <AccordionSummary
      sx={{
        paddingX: "0.5em",
        borderRadius: "0.5em",
        backgroundColor: isExpanded ? "primary.50" : "inherit",
        border: isExpanded
          ? `1px solid ${theme.palette.primary[50]}`
          : `1px solid ${theme.palette.grey[300]}`
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Icon
          icon={icon}
          sx={{
            fontSize: "1.5em",
            color: "primary.main",
            marginRight: "0.25em"
          }}
        />
        <Typography
          sx={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            width: "240px",
            [theme.breakpoints.up("sm")]: {
              width: "450px"
            }
          }}
        >
          {text}
        </Typography>
      </Box>
    </AccordionSummary>
  );
};
